<template>
    <div class="col-12 nopads">
        <mixed-waste-bin-filter @filters-updated="updateFilters"/>
        <mixed-waste-bins-wizard
            v-if="selectedMixedWasteBin != null"
            :mixed-waste-bin="selectedMixedWasteBin"
            :coordinate="showMixedWasteBinWizardOnCoord"
            @close="selectedMixedWasteBin=null"
            @close-and-update="closeAndUpdate"
        />
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {timeUtils} from '../mixins/TimeUtils'
import {mapHelper} from '../mixins/MapMixin'
import {restApi} from '../mixins/RestApiMixin'
import {mixedWasteBinHelper} from "@/components/mixins/MixedWasteBinMixin.js";
import MixedWasteBinFilter from '@/components/mixedwastebins/MixedWasteBinsFilter';
import {geometryMixin} from "@/components/mixins/GeometryMixin";
import MixedWasteBinsWizard from "@/components/mixedwastebins/MixedWasteBinsWizard.vue";
import {inject} from "vue";
import {mixedWastBinWasteType} from "../mixins/MixedWasteBinMixin.js";

// Import all images from roadsign directory
import blueBin from '@/assets/bin-blue.svg'
import greenBin from '@/assets/bin-green.svg'
import brownBin from '@/assets/bin-brown.svg'
import grayBin from '@/assets/bin-gray.svg'

export default {
    name: 'MixedWasteBinsMap',
    components: {MixedWasteBinsWizard, MixedWasteBinFilter},
    mixins: [timeUtils, mapHelper, restApi, geometryMixin],
    props: {
        map: {
            type: Object,
            default: null
        },
        boundingBox: {
            type: Array,
            default: null
        },
        infoVisible: {
            type: Boolean,
            default: true
        },
        zoomOnDrawMarker: {
            type: Boolean,
            default: false
        },
        draggable: {
            type: Boolean,
            default: false
        },
        selectedContractIds: {
            type: Array,
            default() {
                return []
            }
        },
        selectedContractId: {
            type: Number,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            allWasteBins: [],
            typeEntries: [],
            selectedTypes: [],
            selectedBinTypes: [],
            status: 1,
            listOpen: false,
            allSelected: true,
            indeterminate: false,
            selectedMixedWasteBin: null,
            visibilityZoomLevel: 15,
            showMixedWasteBinWizardOnCoord: null,
            contract: null,
            svgUrls: {}
        }
    },
    watch: {
        map() {
            this.$nextTick(function () {
                if (this.boundingBox) {
                    this.initView()
                }
            })
        },
        boundingBox: function () {
            if (this.map) {
                this.initView()
            }
        },
        selectedContractIds() {
            if (this.map) {
                this.initView(true)
            }
        },
        selectedTypes(newVal) {
            if (newVal.length === 0) {
                this.indeterminate = false
                this.allSelected = false
            } else if (newVal.length === this.typeEntries.length) {
                this.indeterminate = false
                this.allSelected = true
            } else {
                this.indeterminate = true
                this.allSelected = false
            }
            this.filterByTypes()
        }
    },
    mounted() {
        // Init icons
        this.svgUrls.blue = blueBin
        this.svgUrls.green = greenBin
        this.svgUrls.brown = brownBin
        this.svgUrls.gray = grayBin

        if (this.map && this.boundingBox) {
            this.initView()
        }
        if(this.selectedContract && Object.keys(this.selectedContract).length !== 0) {
            this.contract = this.selectedContractID
        }
    },
    beforeUnmount() {
        this.hideBins()
    },
    methods: {
        editMixedWasteBin(id) {
            this.loading = true
            this.restFetch(this.mixedWasteBinUrl + '/' + id, this.handleEdit)
        },
        updateFilters(status, filters) {
            this.status = status
            this.selectedBinTypes = filters
            this.hideBins()
            this.initView(true)
        },
        updateView() {
            this.hideBins()
            this.initView(true)
        },
        initView: function (force) {
           if (this.map.getMapZoomLevel() < this.visiblityZoomLevel) {
               this.bbox = null
               this.hideBins()
           } else if (this.boundingBox && ((force || !this.bbox || this.isBboxOutsideBbox(this.boundingBox, this.bbox)))) {
                const extendRate = 0.003
                this.bbox = [
                    this.boundingBox[0] - extendRate,
                    this.boundingBox[1] - extendRate,
                    this.boundingBox[2] + extendRate,
                    this.boundingBox[3] + extendRate
                ]
                this.fetchBins()
            }
        },
        fetchBins() {
            let params = {}
            if (this.selectedBinTypes && this.selectedBinTypes.length > 0) {
                let typeValues = []
                this.selectedBinTypes.forEach(type => {
                    typeValues.push(type.value)
                })
                params.type = typeValues
            }
            params.contract = this.selectedContractIds
            params.bbox = this.bbox
            if (this.status === 0) {
                this.status = 0
            } else if (!this.status) {
                this.status = 1
            }
            params.status = this.status
            this.hideBins()
            this.loading = true
            this.restFetchParams(this.mixedWasteBinMapUrl, params, this.handleSearch)
        },

        handleSearch(response) {
            this.loading = false
            if (response && response.data) {
                this.allWasteBins = response.data
                if (this.allWasteBins && this.allWasteBins.length > 0) {
                    this.drawBins()
                }
            }
        },

        handleEdit(response) {
            this.loading = false
            if(response && response.data) {
                this.selectedMixedWasteBin = response.data
            }
        },

        drawBins: function () {
            this.allWasteBins.forEach(function (bin) {
                this.drawBin(bin)
            }, this)
        },

        drawBin: function (bin) {
            const markerStore = this.map.getMarkerStore()
            const svgUrl = this.getMapIcon(bin)
            const icon = markerStore.getSvgMarker(svgUrl, null, null, '2.7em', '2.7em');
            if (bin?.geom_point?.x != null && bin?.geom_point?.y != null) {
                this.map.showMapMarker(bin.id, this.MIXED_WASTE_BINS, bin.geom_point.y, bin.geom_point.x, icon)
            }
        },

        getMapIcon(bin) {
            switch (bin?.waste_type_id) {
                case mixedWastBinWasteType.BIOLOGICAL: return this.svgUrls.green
                case mixedWastBinWasteType.DOG_LITTER_BOX: return this.svgUrls.brown
                case mixedWastBinWasteType.ASHTRAY: return this.svgUrls.gray
            }
            return this.svgUrls.blue
        },

        hideBins: function () {
            if (this.map) {
                this.map.removeMapItemsByType(this.MIXED_WASTE_BINS)
            }
        },

        toggleList: function () {
            this.listOpen = !this.listOpen
        },

        getVisibleMixedWasteBins() {
            return this.allWasteBins
        },

        closeAndUpdate: function() {
            this.selectedMixedWasteBin = null
            this.showAlert(this.$t('common.alert_update_success'), 'success')
            this.initView(true)

        }

    }
}
</script>
